import React, { useEffect, useState } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  Label,
  Row,
  Col,
  FormFeedback,
  FormGroup,
} from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import { useDispatch, useSelector } from "react-redux"
import {
  createTransferToFacility,
  getTransferProvinces,
  getTransferFacilities,
} from "store/transfer-to-facility/transfer-to-facility-action"
import { getProvinceIns } from "store/province-in/province-in-action"

const TransferModal = ({ isOpen, toggle, receiptData }) => {
  const dispatch = useDispatch()
  const { provinces, facilities } = useSelector(state => state.transferToFacility)

  // Form validation schema
  const validationSchema = Yup.object({
    province_id: Yup.number().required("Province is required"),
    facility_id: Yup.number().required("Facility is required"),
    date: Yup.date().required("Date is required"),
  })

  // Formik form
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      province_id: "",
      facility_id: "",
      date: new Date().toISOString().split('T')[0],
    },
    validationSchema: validationSchema,
    onSubmit: values => {
      handleSubmit(values)
    },
  })

  // Fetch provinces when modal opens
  useEffect(() => {
    if (isOpen) {
      dispatch(getTransferProvinces())
      // Reset facility when province changes
      formik.setFieldValue("facility_id", "")
    }
  }, [isOpen])

  // Fetch facilities when province is selected
  useEffect(() => {
    if (formik.values.province_id) {
      dispatch(getTransferFacilities(formik.values.province_id))
      formik.setFieldValue("facility_id", "")
    } else {
      dispatch(getTransferFacilities(null)) // Clear facilities
    }
  }, [formik.values.province_id])

  // Handle form submission
  const handleSubmit = async values => {
    const data = {
      province_in_id: receiptData?.unique_id,
      province_id: values.province_id,
      facility_id: values.facility_id,
      date: values.date,
    }

    dispatch(
      createTransferToFacility(data, success => {
        if (success) {
          formik.resetForm()
          toggle()
          // Refresh ProvinceIn list
          dispatch(getProvinceIns())
        }
      })
    )
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="md">
      <ModalHeader toggle={toggle}>Transfer to Facility</ModalHeader>
      <Form onSubmit={formik.handleSubmit}>
        <ModalBody>
          {receiptData && (
            <div className="mb-3">
              <p>
                <strong>Batch Number:</strong> {receiptData.batch_number}
              </p>
              <p>
                <strong>Available Quantity:</strong> {receiptData.in_qty}
              </p>
            </div>
          )}

          <Row>
            <Col md={12}>
              <FormGroup>
                <Label>Province *</Label>
                <select
                  className={`form-select ${
                    formik.touched.province_id && formik.errors.province_id
                      ? "is-invalid"
                      : ""
                  }`}
                  name="province_id"
                  value={formik.values.province_id}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={provinces.loading}
                >
                  <option value="">Select Province</option>
                  {provinces.data.map(province => (
                    <option key={province.value} value={province.value}>
                      {province.label}
                    </option>
                  ))}
                </select>
                {formik.touched.province_id && formik.errors.province_id && (
                  <FormFeedback type="invalid">
                    {formik.errors.province_id}
                  </FormFeedback>
                )}
              </FormGroup>
            </Col>

            <Col md={12}>
              <FormGroup>
                <Label>Date *</Label>
                <input
                  type="date"
                  className={`form-control ${
                    formik.touched.date && formik.errors.date
                      ? "is-invalid"
                      : ""
                  }`}
                  name="date"
                  value={formik.values.date}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.date && formik.errors.date && (
                  <FormFeedback type="invalid">
                    {formik.errors.date}
                  </FormFeedback>
                )}
              </FormGroup>
            </Col>

            {formik.values.province_id && (
              <Col md={12}>
                <FormGroup>
                  <Label>Facility *</Label>
                  <select
                    className={`form-select ${
                      formik.touched.facility_id && formik.errors.facility_id
                        ? "is-invalid"
                        : ""
                    }`}
                    name="facility_id"
                    value={formik.values.facility_id}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={facilities.loading}
                  >
                    <option value="">Select Facility</option>
                    {facilities.data.map(facility => (
                      <option key={facility.value} value={facility.value}>
                        {facility.label}
                      </option>
                    ))}
                  </select>
                  {formik.touched.facility_id && formik.errors.facility_id && (
                    <FormFeedback type="invalid">
                      {formik.errors.facility_id}
                    </FormFeedback>
                  )}
                </FormGroup>
              </Col>
            )}
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button type="button" color="secondary" onClick={toggle}>
            Cancel
          </Button>
          <Button type="submit" color="primary">
            Submit Transfer
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  )
}

export default TransferModal
